import { useState, useEffect, useRef } from 'react'
import { ConsumerSimulator } from '../utils/simulator/v2/ConsumerSimulator'
import YouCard from '../components/simulator/YouCard'
import PoolCard from '../components/simulator/PoolCard'
import EventTriggers from '../components/simulator/EventTriggers'
import VersionTabs from '../components/simulator/VersionTabs'
import MetaTags from '../components/shared/MetaTags'
import MacroSimulatorV1 from './MacroSimulatorV1'
import LifeSimulation from '../components/simulator-v3/LifeSimulation.jsx'

export default function MacroSimulatorPage() {
  const [activeVersion, setActiveVersion] = useState('v0.3')
  const [simulatorState, setSimulatorState] = useState({
    // Initialize with default state
    you: {
      premium: 1200,
      hasClaimThisYear: false,
      yearsWithoutClaim: 3,
      score: 0,
      level: 1
    },
    pool: {
      totalMembers: 10000,
      totalPremiums: 12000000,
      totalClaims: 10000000,
      lossRatio: 83.3,
      healthStatus: 'healthy'
    },
    premiumRate: 100,
    gameMode: true,
    scenario: null,
    achievements: []
  })
  const simulatorRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    // Only initialize for v0.2 (v0.3 has its own component)
    if (activeVersion !== 'v0.2') {
      // Clean up if switching away from v0.2
      if (simulatorRef.current) {
        simulatorRef.current.cleanup()
        simulatorRef.current = null;
      }
      return;
    }

    // Small delay to ensure container is rendered
    const timer = setTimeout(() => {
      if (containerRef.current && !simulatorRef.current) {
        console.log('📊 Initializing simulator for version:', activeVersion);
        // Initialize consumer simulator v0.2
        const simulator = new ConsumerSimulator(
          containerRef.current,
          (state) => setSimulatorState(state),
          activeVersion // Pass version to simulator
        );
        simulatorRef.current = simulator;
        console.log('📊 Simulator initialized with version:', simulator.version);
        // Simulator starts automatically in constructor
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [activeVersion]);

  const triggerEvent = (eventType) => {
    if (simulatorRef.current && simulatorRef.current.triggerEvent) {
      simulatorRef.current.triggerEvent(eventType);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto p-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Insurance Pool Simulator
          </h1>
          <p className="text-lg text-gray-300 mb-6">
            Understand why your insurance premium increases even when you don't file claims
          </p>

          {/* Version Tabs */}
          <VersionTabs
            activeVersion={activeVersion}
            onVersionChange={setActiveVersion}
          />
        </div>

        {/* Version-specific content */}
        {activeVersion === 'v0.1' && (
          <div>
            <MetaTags
              title="Insurance Simulator v0.1 (Archived) | deedslab.com"
              description="Original technical implementation with particle physics visualization of insurance pools."
              url="/experiments/macro-insurance-simulator"
              type="website"
            />
            <MacroSimulatorV1 />
          </div>
        )}

        {activeVersion === 'v0.3' && (
          <div>
            <MetaTags
              title="Lives in Motion - Insurance Simulator v0.3 | deedslab.com"
              description="An interactive life simulation exploring risk, resilience, and collective responsibility."
              url="/experiments/macro-insurance-simulator"
              type="website"
            />
            <LifeSimulation />
          </div>
        )}

        {activeVersion === 'v0.2' && (
          <>
            <MetaTags
              title={`Insurance Simulator ${activeVersion} | deedslab.com`}
              description="Interactive insurance pool simulator showing why premiums rise when you don't file claims."
              url="/experiments/macro-insurance-simulator"
              type="website"
            />

            {/* Version indicator - Only show for v0.2 */}
            <div className="mb-6 p-4 bg-gradient-to-r from-green-900/20 to-blue-900/20 rounded-lg border border-green-500/20">
              <span className="text-sm text-[#4ade80] font-mono font-bold">📊 v0.2 - Consumer Edutainment</span>
            </div>

            {/* Simulator Container */}
            <div ref={containerRef} className="w-full h-[600px] bg-black rounded-xl overflow-hidden mb-8 shadow-2xl border border-gray-800">
              {/* Simulator will be rendered here */}
            </div>

            {/* Cards Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <YouCard data={simulatorState} />
              <PoolCard data={simulatorState} />
            </div>

            {/* Event Triggers */}
            <EventTriggers onTriggerEvent={triggerEvent} />
          </>
        )}
      </div>
    </div>
  )
}